import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { marketing } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Loader2, CheckCircle2, Sparkles } from 'lucide-react';

// Public survey view at /s/:slug. Guest-safe. Members earn +50 pts.
// When arriving from a landing-page opt-in, the URL carries `?email=…&name=…`
// so we pre-fill and skip re-typing.
export default function SurveyPublic() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [survey, setSurvey] = useState(null);
  const [err, setErr] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const isAnonymous = searchParams.get('anon') === '1';

  useEffect(() => {
    marketing.publicSurvey(slug).then(setSurvey).catch(e => setErr(e?.response?.data?.detail || 'Not found'));
  }, [slug]);

  useEffect(() => {
    if (user) { setEmail(user.email || ''); setName(user.name || ''); return; }
    const qsEmail = searchParams.get('email');
    const qsName = searchParams.get('name');
    if (qsEmail && !email) setEmail(qsEmail);
    if (qsName && !name)   setName(qsName);
  }, [user, searchParams]);  // eslint-disable-line

  const setAns = (qid, v) => setAnswers(p => ({ ...p, [qid]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Anonymous flow from a landing-page opt-in: the placeholder email that
    // arrives in the URL is enough — no need to prompt again.
    if (!user && !isAnonymous && !email.trim()) { toast({ title: 'Email is required' }); return; }
    // Required-question check
    const missing = (survey.questions || []).find(q => q.required && (answers[q.id] === undefined || answers[q.id] === '' || (Array.isArray(answers[q.id]) && answers[q.id].length === 0)));
    if (missing) { toast({ title: 'Please answer required questions', description: missing.prompt }); return; }
    setBusy(true);
    try {
      const payload = {
        email: email || undefined, name: name || undefined,
        answers: (survey.questions || []).map(q => ({ questionId: q.id, value: answers[q.id] ?? null })),
      };
      const r = await marketing.submitSurvey(slug, payload);
      setDone(r);
      if (r.pointsAwarded > 0) toast({ title: `+${r.pointsAwarded} loyalty pts — thank you!` });
    } catch (e2) {
      toast({ title: 'Submit failed', description: e2?.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  if (err) return <div className="max-w-md mx-auto pbb-card p-8 text-center text-gray-300" data-testid="survey-public-error">{err}</div>;
  if (!survey) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  if (done) return (
    <div className="max-w-md mx-auto pbb-card p-8 text-center space-y-3" data-testid="survey-public-thanks">
      <CheckCircle2 className="mx-auto text-green-400" size={38}/>
      <div className="text-white font-bold text-xl">Thanks for your answers!</div>
      {done.pointsAwarded > 0 && (
        <div className="text-sm text-gray-300 inline-flex items-center gap-1 justify-center"><Sparkles size={12} className="text-yellow-400"/> +{done.pointsAwarded} loyalty points added to your profile</div>
      )}
      {!done.isMember && (
        <Link to={`/signup?email=${encodeURIComponent(email)}`} data-testid="survey-signup-cta"
          className="pbb-btn-green inline-flex mt-3">Complete your PBB signup</Link>
      )}
    </div>
  );

  return (
    <div className="max-w-xl mx-auto space-y-4" data-testid="survey-public-page">
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-widest">{survey.business?.name}</div>
        <h1 className="text-2xl font-bold text-white" data-testid="survey-public-title">{survey.title}</h1>
        {survey.description && <p className="text-sm text-gray-400 mt-1">{survey.description}</p>}
        <p className="text-[11px] text-yellow-300 mt-2 inline-flex items-center gap-1"><Sparkles size={11}/> Earn +50 loyalty points for completing this survey.</p>
      </div>
      <form onSubmit={submit} className="pbb-card p-5 space-y-4" data-testid="survey-public-form">
        {!user && !isAnonymous && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pb-2 border-b border-[#1a1a1a]">
            <input required type="email" placeholder="you@example.com" value={email}
              onChange={e => setEmail(e.target.value)}
              data-testid="survey-public-email"
              className="pbb-input"/>
            <input placeholder="Your name (optional)" value={name}
              onChange={e => setName(e.target.value)}
              data-testid="survey-public-name"
              className="pbb-input"/>
          </div>
        )}
        {(survey.questions || []).map((q, i) => (
          <QuestionInput key={q.id} q={q} value={answers[q.id]} onChange={v => setAns(q.id, v)} index={i}/>
        ))}
        <button type="submit" disabled={busy} data-testid="survey-public-submit"
          className="pbb-btn-green inline-flex items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} Submit &amp; earn 50 pts
        </button>
      </form>
    </div>
  );
}

function QuestionInput({ q, value, onChange, index }) {
  return (
    <div data-testid={`survey-q-${index}`}>
      <div className="text-sm font-semibold text-white">
        {q.prompt} {q.required && <span className="text-red-400">*</span>}
      </div>
      {q.type === 'single_choice' && (
        <div className="mt-2 space-y-1">
          {(q.options || []).map((o, oi) => (
            <label key={oi} className="flex items-center gap-2 text-sm text-gray-200">
              <input type="radio" checked={value === o} onChange={() => onChange(o)}
                data-testid={`survey-q-${index}-opt-${oi}`}
                className="accent-green-500"/> {o}
            </label>
          ))}
        </div>
      )}
      {q.type === 'multi_choice' && (
        <div className="mt-2 space-y-1">
          {(q.options || []).map((o, oi) => {
            const arr = Array.isArray(value) ? value : [];
            const on = arr.includes(o);
            return (
              <label key={oi} className="flex items-center gap-2 text-sm text-gray-200">
                <input type="checkbox" checked={on}
                  onChange={() => onChange(on ? arr.filter(x => x !== o) : [...arr, o])}
                  data-testid={`survey-q-${index}-opt-${oi}`}
                  className="accent-green-500"/> {o}
              </label>
            );
          })}
        </div>
      )}
      {q.type === 'rating' && (
        <div className="mt-2 flex gap-2">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} type="button" onClick={() => onChange(n)}
              data-testid={`survey-q-${index}-rating-${n}`}
              className={`w-9 h-9 rounded-lg border text-sm font-semibold ${value === n ? 'border-green-500 bg-green-500/20 text-white' : 'border-[#2a2a2a] text-gray-400 hover:border-gray-500'}`}>
              {n}
            </button>
          ))}
        </div>
      )}
      {q.type === 'text' && (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)}
          data-testid={`survey-q-${index}-text`}
          rows={3} maxLength={2000} className="pbb-input mt-2"/>
      )}
    </div>
  );
}
